import React, { useMemo } from 'react'
import useSWR from 'swr'
import { useTranslation } from 'react-i18next'
import { Accordion, Card } from 'react-bootstrap'
import {
  faHourglassHalf,
  faCheckSquare,
  faMegaphone,
  faExclamationSquare,
  faCalendarTimes,
} from '@fortawesome/pro-solid-svg-icons'
import Loader from 'components/ui/loader/Loader'
import { paths } from 'routes/paths'
import myCoursesList from 'api/home/MyCourses'
import { EmptyStateBanner } from 'components/ui/empty-states/empty-state-banner'
import { Carousel as CoursesCarousel } from '../../../courses-carousel/Carousel'
import { FolderHeader } from '../../atoms/FolderHeader'
import { AccordionCard } from '../../../../../ui/AccordionCard'
import { AccordionToggle } from '../../../../../ui/AccordionToggle'
import { CourseStatus } from './types'
import styles from './styles.module.scss'

const ByStatus: React.FC<{
  search: string
}> = ({ search }) => {
  const { t } = useTranslation()

  const { data: byStatusData, isLoading: byStatusLoading } = useSWR(
    { key: 'myCoursesListData', search: search },
    async (arg) => myCoursesList({ search: arg.search })
  )

  const statusData = useMemo(() => {
    if (!byStatusData) return {}
    const { inProgress, pending, finish, imparted, soon } = byStatusData
    return {
      inProgress: {
        courses: inProgress.courses,
        total: inProgress.total,
      },
      pending,
      soon,
      finished: finish,
      closed: imparted,
    }
  }, [byStatusData])

  const coursesStatus: CourseStatus[] = useMemo(() => {
    const { inProgress, pending, finished, closed, soon } = statusData
    return [
      {
        status: 'in_progress',
        iconColor: '#D48806',
        icon: faHourglassHalf,
        title: t('home.lmsIndex.lmsMyCourses.obligatoryCoursesTitle'),
        data: inProgress,
        emptyState: {
          text: t(
            'home.lmsIndex.lmsMyCourses.emptyStates.byState.inProgress.text'
          ),
          subtext: t(
            'home.lmsIndex.lmsMyCourses.emptyStates.byState.inProgress.subtext'
          ),
          link: {
            href: paths.library.root,
            text: t(
              'home.lmsIndex.lmsMyCourses.emptyStates.byState.inProgress.link'
            ),
          },
        },
      },
      {
        status: 'pending',
        iconColor: '#FF5B55',
        icon: faExclamationSquare,
        title: t('home.lmsIndex.lmsMyCourses.pendingCourses'),
        data: pending,
        emptyState: {
          text: t(
            'home.lmsIndex.lmsMyCourses.emptyStates.byState.pending.text'
          ),
        },
      },
      {
        status: 'finished',
        iconColor: '#00B47C',
        icon: faCheckSquare,
        title: t('home.lmsIndex.lmsMyCourses.allMyCoursesTitle'),
        data: finished,
        emptyState: {
          text: t(
            'home.lmsIndex.lmsMyCourses.emptyStates.byState.finished.text'
          ),
          subtext: t(
            'home.lmsIndex.lmsMyCourses.emptyStates.byState.finished.subtext'
          ),
        },
      },
      {
        status: 'upcoming',
        iconColor: '#213F68',
        icon: faMegaphone,
        title: t('home.lmsIndex.lmsMyCourses.upcomingCoursesTitle'),
        data: soon,
        emptyState: {
          text: t(
            'home.lmsIndex.lmsMyCourses.emptyStates.byState.upcoming.text'
          ),
          subtext: t(
            'home.lmsIndex.lmsMyCourses.emptyStates.byState.upcoming.subtext'
          ),
          link: {
            href: paths.library.root,
            text: t(
              'home.lmsIndex.lmsMyCourses.emptyStates.byState.upcoming.link'
            ),
          },
        },
      },
      {
        status: 'closed',
        iconColor: '#213F68',
        icon: faCalendarTimes,
        title: t('home.lmsIndex.lmsMyCourses.closedCoursesTitle'),
        data: closed,
        emptyState: {
          text: t('home.lmsIndex.lmsMyCourses.emptyStates.byState.closed.text'),
          subtext: t(
            'home.lmsIndex.lmsMyCourses.emptyStates.byState.closed.subtext'
          ),
          link: {
            href: paths.library.root,
            text: t(
              'home.lmsIndex.lmsMyCourses.emptyStates.byState.closed.link'
            ),
          },
        },
      },
    ]
  }, [statusData, t])

  return (
    <>
      {byStatusLoading ? (
        <Loader color />
      ) : (
        <>
          {coursesStatus.map(
            ({ data, status, iconColor, icon, title, emptyState }, index) => {
              const { courses, total } = { ...data }
              return (
                <Accordion key={index} defaultActiveKey={index.toString()}>
                  <AccordionCard>
                    <AccordionToggle eventKey={index.toString()}>
                      <FolderHeader
                        count={total || 0}
                        iconColor={iconColor}
                        icon={icon}
                        title={title}
                        href={paths.myCourses.advanceCourses.details(status)}
                      />
                    </AccordionToggle>
                    <Accordion.Collapse eventKey={index.toString()}>
                      <Card.Body
                        className={`px-0 ${styles.myCoursesContainer}`}
                      >
                        {courses && courses.length > 0 ? (
                          <CoursesCarousel data={courses} />
                        ) : (
                          <EmptyStateBanner {...emptyState} />
                        )}
                      </Card.Body>
                    </Accordion.Collapse>
                  </AccordionCard>
                </Accordion>
              )
            }
          )}
        </>
      )}
    </>
  )
}

export default ByStatus
